import { ContractStep, ContractStepsNames } from "./contractSteps";
import { shouldHaveOwnershipTransfer } from "./rules/shouldHaveOwnershipTransfer";
import { shouldHavePaymentMethod } from "./rules/shouldHavePaymentMethod";
import { getStepOrderByName } from "./utils/getStepOrderByName";

import { Contract } from "@/components/Contracts/contract.interface";
import { BusinessUnitPaymentMethod } from "@/modules/Settings/pages/BusinessUnits/types/types";
import { BusinessUnit } from "@/modules/User/types/types";

type VerifyContractStepsProps = {
  selectedBU: BusinessUnit;
  setMaxStep: (step: number) => void;
  currentContract: Contract;
  setCurrentStep?: (step: number) => void;
  currentStepOrder: number;
  contractSteps: ContractStep[];
};

const isAccountFilled = (
  currentContract: Contract,
  selectedBU: BusinessUnit
): boolean => {
  const { account, accountDocument, accountContact, checkingAccount } =
    currentContract;

  if (!selectedBU) {
    return false;
  }

  const paymentMethodIsNotDebitAutomatic =
    selectedBU.paymentMethod !== BusinessUnitPaymentMethod.DEBITO_AUTOMATICO;

  const validateCheckingAccount =
    paymentMethodIsNotDebitAutomatic || Boolean(checkingAccount);

  const isAccountFilled =
    account && accountDocument && accountContact && validateCheckingAccount;

  return isAccountFilled;
};

export const verifyContractSteps = ({
  selectedBU,
  setMaxStep,
  currentContract,
  setCurrentStep,
  currentStepOrder,
  contractSteps
}: VerifyContractStepsProps) => {
  const goToStep = ({
    currentStep,
    maxStep
  }: {
    currentStep: ContractStepsNames;
    maxStep?: ContractStepsNames;
  }) => {
    const DEFAULT_MAX_STEP = ContractStepsNames.BENEFICIOS;
    const defaultMaxStepOrder = getStepOrderByName(
      currentContract,
      DEFAULT_MAX_STEP
    );
    const maxStepOrder = getStepOrderByName(currentContract, maxStep);
    const currentStepOrder = getStepOrderByName(currentContract, currentStep);

    setCurrentStep?.(currentStepOrder);
    setMaxStep(maxStep ? maxStepOrder : defaultMaxStepOrder);
  };

  const currentStep = contractSteps?.[currentStepOrder];
  const accountFilled = isAccountFilled(currentContract, selectedBU);
  const hasItems = currentContract.items?.length > 0;
  const hasShipping = Boolean(currentContract?.shipping);
  const contractHasOwnershipTransfer =
    shouldHaveOwnershipTransfer(currentContract);

  const needToAssignPaymentMethod = shouldHavePaymentMethod(
    currentContract,
    currentStep
  );

  if (!accountFilled) {
    goToStep({ currentStep: ContractStepsNames.COOPERADO });

    return;
  }

  if (!hasItems || !hasShipping) {
    goToStep({ currentStep: ContractStepsNames.BENEFICIOS });

    return;
  }

  const hasTermDataDraft = currentContract.termsDataDraft?.length > 0;

  if (contractHasOwnershipTransfer && !hasTermDataDraft) {
    goToStep({
      currentStep: ContractStepsNames.DOCUMENTOS_COMPLEMENTARES,
      maxStep: ContractStepsNames.DOCUMENTOS_COMPLEMENTARES
    });

    return;
  }

  if (needToAssignPaymentMethod) {
    goToStep({
      currentStep: ContractStepsNames.METODO_DE_PAGAMENTO,
      maxStep: ContractStepsNames.METODO_DE_PAGAMENTO
    });

    return;
  }

  if (accountFilled && hasItems && !needToAssignPaymentMethod) {
    goToStep({
      currentStep: ContractStepsNames.CONFIRMACAO,
      maxStep: ContractStepsNames.CONFIRMACAO
    });
  }
};
